import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { Button, Input, Select, InputNumber, notification, Popconfirm, Spin } from "antd";
import type { JobRound, RoundTemplate, CriteriaSchemaItem } from "../../../../core/data/redux/recruitment/types";
import CriteriaBuilderModal from "./CriteriaBuilderModal";
import { useDispatch, useSelector } from "react-redux";
import { saveJobRounds, fetchJobRounds } from "../../../../core/data/redux/recruitment/thunks";
import type { AppDispatch } from "../../../../core/data/redux/store";
import RoundTemplateSelector from "./RoundTemplateSelector";

const { Option } = Select;

interface DragDropRoundBuilderProps {
  jobId: number;
}

const DragDropRoundBuilder: React.FC<DragDropRoundBuilderProps> = ({ jobId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const storedRounds: JobRound[] = useSelector((state: any) => state.recruitment.jobRounds);
  const loading = useSelector((state: any) => state.recruitment.jobRoundsLoading);

  const [rounds, setRounds] = useState<Partial<JobRound>[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRoundIndex, setEditingRoundIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Initialize from Redux
  useEffect(() => {
    dispatch(fetchJobRounds(jobId));
  }, [dispatch, jobId]);

  useEffect(() => {
    if (storedRounds) {
      setRounds(storedRounds.map((r, i) => ({ ...r, stage_order: i + 1 })));
    }
  }, [storedRounds]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(rounds);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update stage sequence orders
    const updated = items.map((r, i) => ({ ...r, stage_order: i + 1 }));
    setRounds(updated);
  };

  const handleApplyTemplate = (template: RoundTemplate) => {
    // Overwrite rounds with template stages
    const newRounds = template.stages.map((stage) => ({
      template_id: template.id,
      round_name: stage.round_name,
      round_type: stage.round_type,
      duration_minutes: stage.duration_minutes,
      stage_order: stage.stage_order,
      criteria_schema: stage.criteria_schema || [],
      is_mandatory: true,
      is_active: true,
    }));
    setRounds(newRounds);
  };

  const addNewRound = () => {
    setRounds([
      ...rounds,
      {
        round_name: `New Round ${rounds.length + 1}`,
        round_type: "video",
        duration_minutes: 60,
        stage_order: rounds.length + 1,
        criteria_schema: [],
        is_mandatory: true,
        is_active: true,
        template_id: null,
      },
    ]);
  };

  const removeRound = (index: number) => {
    const updated = [...rounds];
    updated.splice(index, 1);
    // Refresh stage_order
    updated.forEach((r, i) => { r.stage_order = i + 1; });
    setRounds(updated);
  };

  const updateRound = (index: number, field: keyof JobRound, value: any) => {
    const updated = [...rounds];
    updated[index] = { ...updated[index], [field]: value };
    setRounds(updated);
  };

  const handleOpenCriteria = (index: number) => {
    setEditingRoundIndex(index);
    setModalVisible(true);
  };

  const handleSaveCriteria = (schema: CriteriaSchemaItem[]) => {
    if (editingRoundIndex !== null) {
      updateRound(editingRoundIndex, "criteria_schema", schema);
    }
    setModalVisible(false);
    setEditingRoundIndex(null);
  };

  const saveToBackend = async () => {
    if (rounds.length === 0) {
      notification.warning({ message: "No rounds configured", description: "Add at least one round." });
      return;
    }

    // Validation
    for (let i = 0; i < rounds.length; i++) {
      const r = rounds[i];
      if (!r.round_name) {
        notification.error({ message: "Validation Failed", description: `Round ${i + 1} needs a name.` });
        return;
      }
      if (r.criteria_schema && r.criteria_schema.length > 0) {
        const total = r.criteria_schema.reduce((acc, val) => acc + (val.weight || 0), 0);
        if (Math.abs(total - 100) > 1) {
          notification.error({ message: "Validation Failed", description: `Round ${i + 1} criteria weights must sum to exactly 100.` });
          return;
        }
      }
    }

    try {
      setSaving(true);
      await dispatch(saveJobRounds({ jobId, rounds: rounds as any })).unwrap();
      notification.success({ message: "Success", description: "Job rounds configuration saved!" });
    } catch (e: any) {
      notification.error({ message: "Save Failed", description: e });
    } finally {
      setSaving(false);
    }
  };

  if (loading && rounds.length === 0) return <div className="p-4 text-center"><Spin /></div>;

  return (
    <div>
      <RoundTemplateSelector onSelectTemplate={handleApplyTemplate} />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 fw-bold">Interview Pipeline Sequences</h6>
        <div className="d-flex gap-2">
          <Button onClick={addNewRound} icon={<i className="ti ti-plus" />}>Add Round</Button>
          <Button type="primary" loading={saving} onClick={saveToBackend} icon={<i className="ti ti-device-floppy" />}>
            Save Pipeline
          </Button>
        </div>
      </div>

      {rounds.length === 0 ? (
        <div className="text-center p-5 border rounded bg-light border-dashed">
          <i className="ti ti-git-branch text-muted" style={{ fontSize: 40 }} />
          <h6 className="mt-3 text-muted">No rounds configured</h6>
          <p className="text-muted" style={{ fontSize: 13 }}>Load a template or add a round manually to build your interview pipeline.</p>
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="rounds-droppable">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="d-flex flex-column gap-3">
                {rounds.map((round, index) => (
                  <Draggable key={`round-${index}`} draggableId={`round-${index}`} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="bg-white border rounded shadow-sm"
                        style={{ ...provided.draggableProps.style }}
                      >
                        <div className="d-flex border-bottom p-2 bg-light align-items-center justify-content-between rounded-top">
                          <div className="d-flex align-items-center gap-2">
                            <div {...provided.dragHandleProps} className="px-2" style={{ cursor: "grab" }}>
                              <i className="ti ti-grip-vertical text-muted fs-5" />
                            </div>
                            <span className="badge bg-primary rounded-circle" style={{ width: 24, height: 24, padding: "5px 0" }}>
                              {index + 1}
                            </span>
                            <span className="fw-bold" style={{ fontSize: 13 }}>Stage {index + 1}</span>
                          </div>
                          <div>
                            <Popconfirm title="Remove this round?" onConfirm={() => removeRound(index)} okText="Yes" cancelText="No">
                              <Button type="text" danger icon={<i className="ti ti-trash" />} size="small" />
                            </Popconfirm>
                          </div>
                        </div>

                        <div className="p-3">
                          <div className="row g-3">
                            <div className="col-md-5">
                              <label className="form-label" style={{ fontSize: 12 }}>Round Name</label>
                              <Input 
                                value={round.round_name} 
                                onChange={(e) => updateRound(index, "round_name", e.target.value)} 
                                placeholder="e.g. Technical Assessment" 
                              />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label" style={{ fontSize: 12 }}>Interview Mode</label>
                              <Select 
                                value={round.round_type} 
                                onChange={(val) => updateRound(index, "round_type", val)} 
                                className="w-100"
                              >
                                <Option value="phone"><i className="ti ti-phone me-2" />Phone</Option>
                                <Option value="video"><i className="ti ti-video me-2" />Video</Option>
                                <Option value="in_person"><i className="ti ti-user me-2" />In-Person</Option>
                                <Option value="assignment"><i className="ti ti-file-code me-2" />Assignment</Option>
                              </Select>
                            </div>
                            <div className="col-md-2">
                              <label className="form-label" style={{ fontSize: 12 }}>Duration (mins)</label>
                              <InputNumber 
                                value={round.duration_minutes} 
                                onChange={(val) => updateRound(index, "duration_minutes", val)} 
                                min={15} max={240} 
                                className="w-100" 
                              />
                            </div>
                            <div className="col-md-2 d-flex align-items-end">
                              <Button 
                                type="dashed" 
                                className="w-100" 
                                onClick={() => handleOpenCriteria(index)}
                              >
                                <i className="ti ti-list-check me-1" />
                                Criteria {(round.criteria_schema?.length || 0) > 0 && `(${round.criteria_schema?.length})`}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <CriteriaBuilderModal
        visible={modalVisible}
        initialCriteria={editingRoundIndex !== null ? (rounds[editingRoundIndex].criteria_schema || []) : []}
        onSave={handleSaveCriteria}
        onCancel={() => {
          setModalVisible(false);
          setEditingRoundIndex(null);
        }}
      />
    </div>
  );
};

export default DragDropRoundBuilder;
